import { Component, type ErrorInfo, type ReactNode } from "react";

// ── ErrorBoundary ───────────────────────────────────────────────────────────
//
// Without a boundary, ANY uncaught render error unmounts the whole React tree,
// leaving an empty #root over the app's near-black body background — i.e. a
// silent "black screen" with no diagnostics and no recovery. This boundary
// contains such errors and renders a readable, recoverable panel instead.
//
// SAFETY: the fallback shows only a generic message + the error's own message
// string. It never renders tokens, API URLs, provider ids, or DTOs, and the
// caught error is logged to the console only (not sent anywhere).

interface Props {
  children: ReactNode;
  /** Small label so we can tell WHICH boundary caught it (e.g. "app", "page"). */
  scope?: string;
}
interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Console only — no network, no token/PII. Helps diagnose the black screen.
    console.error(`[calibre] render error caught by ErrorBoundary(${this.props.scope ?? "app"})`, error, info.componentStack);
  }

  private reset = (): void => this.setState({ error: null });

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          minHeight: "100dvh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          background: "#0E0E10",
          color: "#E8E8EE",
          fontFamily: "'DM Sans', system-ui, sans-serif",
        }}
      >
        <div
          style={{
            maxWidth: 460,
            width: "100%",
            background: "#151519",
            border: "1px solid #2A2A32",
            borderRadius: 12,
            padding: "28px 26px",
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>Something went wrong</div>
          <p style={{ fontSize: 13, lineHeight: 1.6, color: "#A8A8B4", margin: "0 0 18px" }}>
            This screen hit an unexpected error and couldn’t render. Your data is safe — try again, or
            reload the page.
          </p>
          {error.message && (
            <pre
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11,
                color: "#8A8A96",
                background: "#0E0E10",
                border: "1px solid #24242C",
                borderRadius: 8,
                padding: "10px 12px",
                margin: "0 0 18px",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                maxHeight: 120,
                overflow: "auto",
              }}
            >
              {error.message}
            </pre>
          )}
          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={this.reset}
              style={{
                cursor: "pointer",
                background: "#12B5A0",
                color: "#03110F",
                border: "none",
                borderRadius: 8,
                padding: "9px 16px",
                fontSize: 13,
                fontWeight: 600,
                fontFamily: "inherit",
              }}
            >
              Try again
            </button>
            <button
              onClick={() => window.location.assign("/")}
              style={{
                cursor: "pointer",
                background: "transparent",
                color: "#E8E8EE",
                border: "1px solid #2A2A32",
                borderRadius: 8,
                padding: "9px 16px",
                fontSize: 13,
                fontWeight: 600,
                fontFamily: "inherit",
              }}
            >
              Reload
            </button>
          </div>
        </div>
      </div>
    );
  }
}
